<script lang="ts" setup>
import { computed } from 'vue';

import type { AffiliateApiFormSchema } from '@modules/affiliate-api/types';
import type { FormSchema } from '@modules/forms/components/VForm/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import { TRANSLATION_GROUP_AFFILIATE_API } from '@modules/affiliate-api/constants';
import { useAffiliateApiResetTokenMutation } from '@modules/affiliate-api/mutations';
import { useAffiliateApiQuery } from '@modules/affiliate-api/queries';
import { useCopyToClipboard } from '@modules/core/composables/copy';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useUserSupervision } from '@modules/user/composables';
import {
  assert,
  isString,
} from '@utils/guards';

const { data, isLoading } = useAffiliateApiQuery();
const { mutate: resetToken } = useAffiliateApiResetTokenMutation();
const { copyToClipboard } = useCopyToClipboard();
const { confirm } = useConfirmDialogs();
const { getTranslation } = useI18n();
const { isSupervision } = useUserSupervision();

const linkValue = computed(() => (data.value?.domain));

function copyLink(): void {
  assert(isString(linkValue.value));
  void copyToClipboard(linkValue.value);
}

function handleClick(): void {
  confirm({
    group: 'confirmDialog',
    header: getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'ANSWER_YES').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'ANSWER_NO').value,
    accept: () => resetToken(),
  });
}

const formSchema = computed<FormSchema<AffiliateApiFormSchema>>(() => ({
  link: {
    type: EFormInputTypes.TEXTAREA,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'LINK').value,
    value: linkValue.value,
    readonly: true,
  },
}));
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateApiPage'"
    type="form"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'HEADER')"
  >
    <VLoader
      :loading="isLoading"
      mode="overlay"
    >
      <VForm
        :schema="formSchema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'COPY')"
        @submit="copyLink"
      >
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_API, 'RESET_BTN')"
            :disabled="isSupervision"
            @click="handleClick"
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
